import React, { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "../layout/AdminLayout";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchUsers,
  addUser,
  updateUser,
} from "../../services/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  FaEllipsisV,
  FaEdit,
  FaEye,
  FaUserSlash,
  FaUserCheck,
} from "react-icons/fa";

const ManageUsers = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const menuRefs = useRef({});

  const queryClient = useQueryClient();

  const {
    data: response,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["hospitalUsers"],
    queryFn: fetchUsers,
  });

  const users = response?.users || response?.data || response?.results || [];
  const totalUsers = response?.total || users.length || 0;

  const filteredUsers = useMemo(() => {
    if (!searchTerm.trim()) return users;
    const term = searchTerm.toLowerCase();
    return users.filter(
      (user) =>
        (user.name || user.full_name || "").toLowerCase().includes(term) ||
        (user.email || "").toLowerCase().includes(term) ||
        (user.patientId || user.hospital_number || "").toLowerCase().includes(term) ||
        (user.phone || user.phone_number || "").toLowerCase().includes(term)
    );
  }, [users, searchTerm]);

  const getInitials = (name = "") => {
    return name
      .trim()
      .split(/\s+/)
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2) || "NA";
  };

  const toggleSelect = (id) => {
    setSelectedUsers((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedUsers.length === filteredUsers.length && filteredUsers.length > 0) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filteredUsers.map((u) => u.id));
    }
  };

  const updateUserMutation = useMutation({
    mutationFn: ({ id, data }) => updateUser(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["hospitalUsers"] });
    },
    onError: (err) => alert("Status update failed: " + err.message),
  });

  const handleToggleStatus = (user) => {
    const newStatus = user.status?.toLowerCase() === "active" ? "inactive" : "active";
    updateUserMutation.mutate({
      id: user.id,
      data: { status: newStatus },
    });
    setOpenMenuId(null);
  };

  const toggleMenu = (userId, e) => {
    e.stopPropagation();
    if (openMenuId === userId) {
      setOpenMenuId(null);
      return;
    }

    const button = menuRefs.current[userId];
    if (button) {
      const rect = button.getBoundingClientRect();
      setMenuPosition({
        top: rect.top + window.scrollY - 10,
        left: rect.right + window.scrollX - 200,
      });
    }
    setOpenMenuId(userId);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(".menu-container") && !e.target.closest(".menu-button")) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Manage Hospital Users</h1>
            <p className="text-gray-600 mt-1">
              Total of {totalUsers} user {totalUsers === 1 ? "account" : "accounts"} · Read-only view
            </p>
          </div>
          <div className="flex gap-3 flex-wrap">
            <Button
              onClick={() => alert("Export feature coming soon")}
              className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
            >
              Export
            </Button>
          </div>
        </div>

        {/* Controls */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
            <div />

            <div className="relative w-full lg:w-80">
              <Input
                type="text"
                placeholder="Search by name, email, patient ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px]">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-4 text-left">
                    <input
                      type="checkbox"
                      checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                      onChange={toggleSelectAll}
                      className="rounded border-gray-300 h-4 w-4"
                    />
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Patient ID</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Wallet Balance</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Joined</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {isLoading ? (
                  <tr>
                    <td colSpan={8} className="text-center py-10">
                      Loading users...
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td colSpan={8} className="text-center py-10 text-red-600">
                      Error: {error.message}
                    </td>
                  </tr>
                ) : filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center py-10 text-gray-500">
                      No users found
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <input
                          type="checkbox"
                          checked={selectedUsers.includes(user.id)}
                          onChange={() => toggleSelect(user.id)}
                          className="rounded border-gray-300 h-4 w-4"
                        />
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-semibold text-sm">
                            {getInitials(user.name || user.full_name)}
                          </div>
                          <span className="font-medium">
                            {user.name || user.full_name || "—"}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-gray-600">{user.email || "—"}</td>
                      <td className="px-6 py-4 text-gray-600">
                        {user.phone || user.phone_number || "—"}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                            (user.status || "").toLowerCase() === "active"
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {(user.status || "unknown").charAt(0).toUpperCase() +
                            (user.status || "unknown").slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4 font-medium">
                        {user.patientId || user.hospital_number || "—"}
                      </td>
                      <td className="px-6 py-4 font-semibold text-green-700">
                        ₦{parseFloat(user.wallet_balance || user.wallet?.balance || 0).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 text-gray-600">
                        {user.joined || user.created_at
                          ? new Date(user.created_at || user.joined).toLocaleDateString()
                          : "—"}
                      </td>
                      <td className="px-6 py-4 text-right relative">
                        <button
                          ref={(el) => (menuRefs.current[user.id] = el)}
                          onClick={(e) => toggleMenu(user.id, e)}
                          className="text-gray-500 hover:text-gray-700 p-2 rounded-full hover:bg-gray-100 transition menu-button"
                        >
                          <FaEllipsisV className="w-5 h-5" />
                        </button>

                        {openMenuId === user.id && (
                          <div
                            className="fixed w-48 bg-white rounded-md shadow-2xl z-[9999] border border-gray-200 menu-container"
                            style={{
                              top: `${menuPosition.top}px`,
                              left: `${menuPosition.left}px`,
                            }}
                          >
                            <div className="py-1">
                              <button
                                onClick={() => {
                                  navigate(`/admin/users/${user.id}`);
                                  setOpenMenuId(null);
                                }}
                                className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100 w-full text-left flex items-center gap-3"
                              >
                                <FaEye className="text-gray-500" /> View Details
                              </button>

                              <button
                                onClick={() => {
                                  navigate(`/admin/transaction/user/${user.id}`);
                                  setOpenMenuId(null);
                                }}
                                className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100 w-full text-left flex items-center gap-3"
                              >
                                <FaEye className="text-gray-500" /> View Transaction Details
                              </button>
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalUsers > 0 && (
            <div className="px-6 py-4 bg-gray-50 border-t flex justify-between items-center text-sm text-gray-600">
              <div>Showing {filteredUsers.length} of {totalUsers} users</div>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default ManageUsers;